/**
 * @dr.pogodin/react-helmet compatibility shim.
 * Applies <title>/<meta>/<link>/<script> children to document.head on the client.
 * SSR renders nothing (page-level SEO falls back to route head() where defined).
 */
import * as React from "react";

interface TagSpec {
  tag: string;
  attrs: Record<string, string>;
  text?: string;
}

const MANAGED_ATTR = "data-rh-shim";

function collect(children: React.ReactNode, acc: TagSpec[], titleRef: { value?: string }) {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const type = child.type as string;
    const props = (child.props ?? {}) as Record<string, unknown>;

    if (type === "title") {
      const t = props.children;
      titleRef.value = Array.isArray(t) ? t.join("") : String(t ?? "");
      return;
    }
    if (type === "meta" || type === "link") {
      const attrs: Record<string, string> = {};
      for (const [k, v] of Object.entries(props)) {
        if (k === "children" || v == null) continue;
        attrs[k] = String(v);
      }
      acc.push({ tag: type, attrs });
      return;
    }
    if (type === "script") {
      const attrs: Record<string, string> = {};
      let text: string | undefined;
      for (const [k, v] of Object.entries(props)) {
        if (k === "children") {
          text = Array.isArray(v) ? v.join("") : String(v ?? "");
          continue;
        }
        if (v == null) continue;
        attrs[k] = String(v);
      }
      acc.push({ tag: "script", attrs, text });
    }
  });
}

export function Helmet({ children }: { children?: React.ReactNode }) {
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const specs: TagSpec[] = [];
    const titleRef: { value?: string } = {};
    collect(children, specs, titleRef);

    const prevTitle = document.title;
    if (titleRef.value != null) document.title = titleRef.value;

    const created: HTMLElement[] = [];
    for (const spec of specs) {
      const el = document.createElement(spec.tag);
      for (const [k, v] of Object.entries(spec.attrs)) {
        el.setAttribute(k === "charSet" ? "charset" : k, v);
      }
      if (spec.text != null) el.textContent = spec.text;
      el.setAttribute(MANAGED_ATTR, "1");
      document.head.appendChild(el);
      created.push(el);
    }

    return () => {
      if (titleRef.value != null) document.title = prevTitle;
      created.forEach((el) => el.remove());
    };
  }, [children]);

  return null;
}

export function HelmetProvider({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
}

export default Helmet;
